import { useConfiguratorStore } from '@/stores/Configurator/ConfiguratorStore';
import { cutoutKey, useFilterStore } from '@/stores/Configurator/FilterStore';
import { useMediaclipStore } from '@/stores/Configurator/MediaclipStore';
import axios from 'axios';
import _ from 'lodash';
import { useMediaclipCommunication } from './UseMediaclipCommunication';

export function useMediaclip() {
  const mediaclipStore = useMediaclipStore();
  const filterStore = useFilterStore();
  const configuratorStore = useConfiguratorStore();

  function handleEvent(event: MessageEvent): void {
    switch (event.data.type) {
      case 'mediaclipToolLoaded':
        handleMediaclipToolLoadedEvent();
        break;
      case 'saved':
        handleSavedEvent();
        break;
      case 'amountOfPhotos':
        handleAmountOfPhotosEvent(event);
        break;
      case 'previousButtonClicked':
        handlePreviousButtonClickedEvent();
        break;
      case 'nextButtonClicked':
        handleNextButtonClickedEvent();
        break;
      case 'deleteButtonClicked':
        mediaclipStore.removePhoto();
        break;
      case 'worksheetSelectorClicked':
        handleWorksheetSelectorClickedEvent(event);
        break;
      case 'worksheetFilled':
        handleWorkareaPhotoElementFilledEvent();
        break;
      case 'onAutofill':
        handleFilledPhotos(event.data.selectedPhotosCount);
        break;
      case 'onAutofillAfterUpload':
        handleFilledPhotos(event.data.selectedPhotosCount);
        break;
      case 'cancelAutofill':
        mediaclipStore.isAutofillCancelled = true;
        break;
      case 'onUploadComplete':
        handleUploadComplete(event.data.url);
        break;
    }
  }

  function handleMediaclipToolLoadedEvent(): void {
    // If we're dealing with a simple configurator, mark project as not updating
    if (configuratorStore.isSimpleConfigurator) {
      configuratorStore.isUpdatingProject = false;
    }
  }

  async function handleSavedEvent(): Promise<void> {
    configuratorStore.isUpdatingProject = false;

    if (
      configuratorStore.isLetterCollageConfigurator ||
      filterStore.hasHexagonsFilter
    ) {
      useMediaclipCommunication().emitEvent('letterCollageLoaded');
    }

    if (configuratorStore.isSimpleConfigurator) {
      await useMediaclipCommunication().setProjectThumbnailUrl();
    }

    if (
      !configuratorStore.hasSavedAtLeastOneTime &&
      configuratorStore.isUploadConfigurator &&
      configuratorStore.configuratorIframe()?.contentWindow
    ) {
      configuratorStore
        .configuratorIframe()
        ?.contentWindow?.postMessage(
          { type: 'isAutoclosingMenu', action: '' },
          '*'
        );

      configuratorStore.hasSavedAtLeastOneTime = true;
    }
  }

  function handleAmountOfPhotosEvent(event: MessageEvent): void {
    configuratorStore.allPicturesUploaded = event.data.hasUploadedAllPhotos;
  }

  function handlePreviousButtonClickedEvent(): void {
    mediaclipStore.watchPreviousButtonClick();
  }

  function handleNextButtonClickedEvent(): void {
    mediaclipStore.watchNextButtonClick();
  }

  function handleWorksheetSelectorClickedEvent(event: MessageEvent): void {
    // Logic to handle the worksheet selector (set current worksheet)
    mediaclipStore.currentWorksheet = event.data.selectedWorksheet;
  }

  function handleWorkareaPhotoElementFilledEvent(): void {
    if (
      !_.includes(
        mediaclipStore.emptyWorksheets,
        mediaclipStore.currentWorksheet
      )
    ) {
      mediaclipStore.addFilledWorksheet();
    }

    if (
      _.includes(
        mediaclipStore.emptyWorksheets,
        mediaclipStore.currentWorksheet
      ) &&
      !_.includes(
        mediaclipStore.filledWorksheets,
        mediaclipStore.currentWorksheet
      )
    ) {
      // Remove from empty worksheets array
      _.remove(
        mediaclipStore.emptyWorksheets,
        worksheet => worksheet === mediaclipStore.currentWorksheet
      );

      // Mark 1st worksheet as filled
      mediaclipStore.addFilledWorksheet();
    }
  }

  function handleFilledPhotos(count: number): void {
    // If autofill is cancelled, reset boolean and do nothing else
    if (mediaclipStore.isAutofillCancelled) {
      // Reset isAutofillCancelled boolean for the next run
      mediaclipStore.isAutofillCancelled = false;
      return;
    }

    // Shift provided count from the empty worksheets array
    _.times(count, () => {
      mediaclipStore.emptyWorksheets.shift();
    });

    // Set current worksheet to the 1st
    mediaclipStore.currentWorksheet = 1;

    // Mark 1st worksheet as filled
    mediaclipStore.addFilledWorksheet();
  }

  async function handleUploadComplete(
    url: string,
    tries: number = 0
  ): Promise<void> {
    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `HubStoreUserToken ${configuratorStore.mediaclipToken}`,
        },
      });

      filterStore.setDefaultFilterSku(cutoutKey, {
        width: response.data.width,
        height: response.data.height,
      });
    } catch (error) {
      if (tries > 5) {
        throw error;
      }

      handleUploadComplete(url, tries + 1);
    }
  }

  return {
    handleEvent,
  };
}
